import React, { useMemo } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Contract, VisibleColumnSpec } from "@/types";
import { useTranslation } from "react-i18next";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);
import DataTable from "@/components/molecules/DataTable";
import { useVisibleColumns } from "@/hooks/useVisibleColumns";
import SortableHeader from "@/components/molecules/SortableHeader";
import { useGetArchivedContracts } from "@/services/Contracts";
import Badge from "@/components/atoms/Badge";
import Group from "@/components/atoms/Group";
import { Menu } from "@mantine/core";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import styles from "./styles.module.css";

interface ContractTableProps {
  /** Define a column spec of what rows and columns to display and when **/
  visibleColumns?: VisibleColumnSpec<Contract, "counterParty" | "product" | "actions">[];
  /** Define a predefined sort order **/
  initialSort?: SortingState;
  /** Whether to show client-side pagination */
  pagination?: boolean;
  /** Decide whether to enable or disable actions **/
  actionable?: boolean;
  /** Optional rows per page for client-side pagination */
  initialRowsPerPage?: number;
}

/**
 * A reusable task table component, with sorting, filtering, actions, and pagination.
 * @param TaskTableProps - Component props
 * @param visibleColumns - Constant Array defined to manage which columns to display and when
 * @param initialSort - Define a predefined sort order
 * @param pagination - Boolean to enable or disable pagination
 * @param actionable - Boolean to enable or disable row actions
 * @param initialRowsPerPage - set maximum number of rows to display (snapshots)
 *
 **/
const ArchivedContractTable: React.FC<ContractTableProps> = ({
  visibleColumns,
  initialSort = null,
  pagination: _p = true,
  actionable: _a = true,
  initialRowsPerPage = 15,
}) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { currentCompany } = useAuth();
  const { data: contracts, isLoading } = useGetArchivedContracts(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });

  const baseColumns: ColumnDef<Contract>[] = useMemo(
    () => [
      // {
      //   header: ({ column }) => <SortableHeader column={column} label={t("contracts:contractDate")} />,
      //   id: "contractDate",
      //   cell: ({ row }) => {
      //     return dayjs(row.original.contractDate).format("Do MMMM YYYY");
      //   },
      //   footer: (props) => props.column.id,
      // },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={"ID"} />,
        id: "id",
        cell: ({ row }) => {
          return row.original.id;
        },
        size: 25,
        minSize: 25,
        maxSize: 25,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("contracts:startDate")} />,
        id: "contractStartDate",
        cell: ({ row }) => {
          return dayjs(row.original.contractStartDate).format("Do MMMM YYYY");
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("contracts:endDate")} />,
        id: "contractEndDate",
        cell: ({ row }) => {
          if (!row.original.contractEndDate) {
            return "";
          } else {
            return dayjs(row.original.contractEndDate).format("Do MMMM YYYY");
          }
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("contracts:archivedDate")} />,
        id: "archivedDate",
        cell: ({ row }) => {
          if (!row.original.archivedDate) {
            return "";
          } else {
            return dayjs(row.original.archivedDate).format("Do MMMM YYYY");
          }
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:product")} />,
        id: "product",
        cell: ({ row }) => {
          const product = row.original.contractProduct.companySpecificProduct;
          return product.companySpecificProductName;
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("contracts:counterParty")} />,
        id: "counterParty",
        cell: ({ row }) => {
          if (row.original.contractSellers[0].id === currentCompany?.companyId) {
            return row.original.contractBuyers[0].companyName;
          } else {
            return row.original.contractSellers[0].companyName;
          }
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:amount")} />,
        id: "amount",
        cell: ({ row }) => {
          if (row.original.contractType === "DATEPERIOD") return "";
          const product = row.original.contractProduct;
          return `${row.original.amount || 0} ${product.priceUnit.unit}`;
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:remainingAmount")} />,
        id: "remainingAmount",
        cell: ({ row }) => {
          if (row.original.contractType === "DATEPERIOD") return "";
          const product = row.original.contractProduct;
          return `${row.original.remainingAmount} ${product.priceUnit.unit}`;
        },
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("contracts:automaticSalesApproval")} />,
        id: "automaticSalesApproval",
        cell: ({ row }) => {
          return row.original.automaticSalesApproval;
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("contracts:contractType")} />,
        id: "contractType",
        cell: ({ row }) => {
          return (
            <Badge
              autoContrast={true}
              color={
                row.original.contractType === "DATEPERIOD"
                  ? "var(--color-brand-reef-strong)"
                  : "var(--color-brand-aqua-100"
              }
            >
              {t("enumerables:contractTypes." + row.original.contractType)}
            </Badge>
          );
        },
      },
      {
        header: () => <></>,
        id: "actions",
        size: 50,
        minSize: 50,
        maxSize: 50,
        cell: ({ row }) => {
          return (
            <Group justify={"flex-end"} w={"100%"}>
              <Menu offset={0} position={"bottom-end"}>
                <Menu.Target>
                  <span style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                    <Group align={"center"} justify={"center"}>
                      <SVGAsset name="dotdotdot-grey" />
                    </Group>
                  </span>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    style={{ width: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/contracts/details/${row.original.id}`);
                    }}
                  >
                    <Group>{t("contracts:showDetails")}</Group>
                  </Menu.Item>
                  {/*}
                  <Menu.Item
                    style={{ width: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      open(row.original.id);
                    }}
                  >
                    <Group>{t("actions:renewContract")}</Group>
                  </Menu.Item>*/}
                </Menu.Dropdown>
              </Menu>
            </Group>
          );
        },
      },
    ],
    [t, currentCompany]
  );

  const columns = useVisibleColumns<Contract, "counterParty" | "product" | "actions">(baseColumns, visibleColumns);

  return (
    <Group dir={"col"} className={styles.tableContainer}>
      <DataTable
        onRowClick={undefined}
        data={contracts || []}
        columns={columns}
        isLoading={isLoading}
        mobileHeaderCell={2}
        showPagination={true}
        initialSort={initialSort ?? undefined}
        initialRowsPerPage={initialRowsPerPage}
      />
    </Group>
  );
};

export default ArchivedContractTable;
